import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2, Mail, Download, FileText, Info, X, ChevronLeft, ChevronRight, ChevronDown,
  Loader2, AlertTriangle, Search, FileSpreadsheet, FileDown, Columns3, Sparkles,
} from "lucide-react";
import api, { erroMsg } from "./api";
import ScreenOverlay from "../../components/common/ScreenOverlay";

/* ---------------- formatação ---------------- */
export const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
export const MESES_CURTOS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
export const TIPOS = ["DARF", "DAS", "DAE", "DJE"];
export const TIPOS_DESC = {
  DARF: "Receitas Federais",
  DAS: "Simples Nacional",
  DAE: "eSocial Doméstico",
  DJE: "Depósitos Judiciais",
};
export const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export const dataBR = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  if (s.includes("/")) return s;
  const [a, m, d] = s.split("-");
  return d ? `${d}/${m}/${a}` : s;
};
export const paBR = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return dataBR(s);
  if (/^\d{6}$/.test(s)) return `${s.slice(4, 6)}/${s.slice(0, 4)}`;
  return s;
};
export const cnpjFmt = (c) => {
  const d = String(c || "").replace(/\D/g, "");
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : c;
};
export const trunc = (s, n = 38) => (s && s.length > n ? s.slice(0, n) + "…" : s);
export const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* ---------------- botões base (padrão Simpler) ---------------- */
const BTN = "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
export const btn = {
  primary: `${BTN} h-9 px-4 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm`,
  outline: `${BTN} h-9 px-3.5 border border-border bg-card text-foreground hover:bg-muted`,
  ghost: `${BTN} h-9 px-3 text-muted-foreground hover:bg-muted hover:text-foreground`,
  icon: `${BTN} h-8 w-8 text-muted-foreground hover:bg-muted hover:text-foreground`,
  soft: `${BTN} h-9 px-3.5 bg-primary/10 text-primary hover:bg-primary/15`,
};
export const inputCls = "h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40";

/* ---------------- Tooltip ---------------- */
export function Tip({ text, children, side = "top" }) {
  return (
    <span className="relative group inline-flex">
      {children}
      <span className={`pointer-events-none absolute left-1/2 -translate-x-1/2 z-50 hidden group-hover:block whitespace-pre-line text-center rounded-md bg-foreground px-2.5 py-1.5 text-[11px] font-medium leading-snug text-background shadow-lg w-max max-w-[200px] ${side === "top" ? "bottom-full mb-2" : "top-full mt-2"}`}>
        {text}
      </span>
    </span>
  );
}

/* ---------------- Célula de tipo (DARF/DAS/DAE/DJE) ---------------- */
export function IconeMoeda({ tipo, qtd, onClick }) {
  const ativo = qtd > 0;
  if (!ativo) {
    return (
      <Tip text={`Nenhum ${tipo} encontrado`}>
        <span className="inline-flex h-7 min-w-[44px] items-center justify-center rounded-full text-xs text-muted-foreground/60">—</span>
      </Tip>
    );
  }
  return (
    <Tip text={`${tipo} pago(s) encontrado(s).\nClique para ver detalhes.`}>
      <button type="button" onClick={onClick}
        className="inline-flex h-7 min-w-[44px] items-center justify-center gap-1 rounded-full border border-success/30 bg-success/10 px-2.5 text-xs font-semibold text-success transition hover:bg-success/20">
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2} />
        {qtd}
      </button>
    </Tip>
  );
}

/* ---------------- Overlays ---------------- */
function OverlayBase({ children, onClose, z = "z-[9990]" }) {
  return (
    <ScreenOverlay><div className={`fixed inset-0 ${z} flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-20 pb-10 backdrop-blur-sm`}
      onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      {children}
    </div></ScreenOverlay>
  );
}

function BuscandoBase({ msg = "Consultando a Receita Federal.\nIsso pode levar alguns segundos..." }) {
  return (
    <ScreenOverlay><div className="fixed inset-0 z-[9995] flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-[340px] rounded-2xl border border-border bg-card p-6 text-center shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-foreground">Buscando...</h3>
        <p className="mt-1 whitespace-pre-line text-xs leading-5 text-muted-foreground">{msg}</p>
      </div>
    </div></ScreenOverlay>
  );
}

function AlertaBase({ titulo, msg, icone, onOk }) {
  const tom = icone === "info"
    ? { bg: "bg-primary/10", fg: "text-primary", Icon: Info }
    : icone === "ok"
      ? { bg: "bg-success/10", fg: "text-success", Icon: CheckCircle2 }
      : { bg: "bg-destructive/10", fg: "text-destructive", Icon: AlertTriangle };
  return (
    <ScreenOverlay><div className="fixed inset-0 z-[9997] flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-[380px] rounded-2xl border border-border bg-card p-6 text-center shadow-xl">
        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${tom.bg}`}>
          <tom.Icon className={`h-6 w-6 ${tom.fg}`} />
        </div>
        <h3 className="mt-4 text-base font-semibold text-foreground">{titulo}</h3>
        {msg && <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{msg}</p>}
        <button onClick={onOk} className={`${btn.primary} mt-5 w-full`}>Entendi</button>
      </div>
    </div></ScreenOverlay>
  );
}

/* ---------------- ações de PDF ---------------- */
export async function abrirPdf(doc, download = false) {
  const r = await api.get(`/doc/${doc.id}/pdf`, { params: { download }, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  if (download) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.tipo || "comprovante"}_${doc.numero_documento}.pdf`;
    a.click();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function whatsapp(doc) {
  const { data } = await api.get(`/doc/${doc.id}/link-publico`);
  const txt = `Comprovante de pagamento ${doc.tipo} - ${doc.razao_social}\nNº ${doc.numero_documento} | Pago em ${dataBR(doc.data_arrecadacao)} | ${brl(doc.valor_total)}\n${data.url}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
}

function ModalEmail({ doc, onClose, setAlerta }) {
  const [para, setPara] = useState("");
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    const lista = para.split(/[;,\s]+/).filter(Boolean);
    if (!lista.length) return;
    setEnviando(true);
    try {
      await api.post(`/doc/${doc.id}/email`, { para: lista });
      setAlerta({ icone: "ok", titulo: "E-mail enviado", msg: `Comprovante enviado para ${lista.join(", ")}.` });
      onClose();
    } catch (e) {
      setAlerta({ titulo: "Não foi possível enviar o e-mail", msg: erroMsg(e) });
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Overlay onClose={onClose} z="z-[9993]">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground">Enviar por e-mail</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{doc.tipo} nº {doc.numero_documento}</p>
          </div>
          <button onClick={onClose} className={btn.icon}><X className="h-4 w-4" /></button>
        </div>
        <label className="mt-5 block text-xs font-medium text-foreground">Destinatário(s)</label>
        <input autoFocus value={para} onChange={(e) => setPara(e.target.value)} placeholder="cliente@empresa.com.br"
          onKeyDown={(e) => e.key === "Enter" && enviar()} className={`${inputCls} mt-1.5`} />
        <p className="mt-1.5 text-[11px] text-muted-foreground">Separe vários e-mails por vírgula.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className={btn.ghost}>Cancelar</button>
          <button disabled={enviando} onClick={enviar} className={btn.primary}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Enviar
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------------- 4 ações ---------------- */
function WhatsIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6a2.7 2.7 0 0 0 1.8-1.2 2.2 2.2 0 0 0 .1-1.3c0-.1-.2-.2-.5-.3Z" /></svg>
  );
}

export function AcoesDocumento({ doc, setAlerta }) {
  const [email, setEmail] = useState(false);
  const ok = doc.pdf_gerado;
  const run = (fn) => async () => {
    if (!ok) return;
    try { await fn(); } catch (e) { setAlerta({ titulo: "Não foi possível abrir o comprovante", msg: erroMsg(e) }); }
  };
  const dica = (t) => (ok ? t : "Gere o comprovante primeiro");
  return (
    <div className="flex items-center gap-0.5">
      <Tip text={dica("Enviar por e-mail")}><button disabled={!ok} className={btn.icon} onClick={() => setEmail(true)}><Mail className="h-4 w-4" /></button></Tip>
      <Tip text={dica("Enviar por WhatsApp")}><button disabled={!ok} className={btn.icon} onClick={run(() => whatsapp(doc))}><WhatsIcon className="h-4 w-4" /></button></Tip>
      <Tip text={dica("Baixar PDF")}><button disabled={!ok} className={btn.icon} onClick={run(() => abrirPdf(doc, true))}><Download className="h-4 w-4" /></button></Tip>
      <Tip text={dica("Visualizar PDF")}><button disabled={!ok} className={btn.icon} onClick={run(() => abrirPdf(doc, false))}><FileText className="h-4 w-4" /></button></Tip>
      {email && <ModalEmail doc={doc} onClose={() => setEmail(false)} setAlerta={setAlerta} />}
    </div>
  );
}

export function useGerar(setBuscando, setAlerta) {
  return async (doc, onUpdate) => {
    setBuscando("Emitindo o comprovante na Receita Federal.\nIsso pode levar alguns segundos...");
    try {
      const { data } = await api.post(`/doc/${doc.id}/gerar`);
      onUpdate(data);
      await abrirPdf(data, false);
    } catch (e) {
      setAlerta({ titulo: "Não foi possível gerar o comprovante", msg: erroMsg(e) });
    } finally {
      setBuscando(null);
    }
  };
}

export function BotaoGerar({ doc, onGerar, small }) {
  if (doc.pdf_gerado) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 font-semibold uppercase tracking-wider text-success ${small ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]"}`}>
        <CheckCircle2 className="h-3 w-3" /> Gerado
      </span>
    );
  }
  return (
    <button onClick={onGerar} className={small ? `${btn.soft} h-8 px-3 text-xs` : btn.primary}>
      <Sparkles className="h-3.5 w-3.5" /> Gerar comprovante
    </button>
  );
}

/* ---------------- Modal de detalhes ---------------- */
export function ModalDetalhes({ empresa, tipo, ano, mes, onClose, setBuscando, setAlerta }) {
  const [docs, setDocs] = useState(null);
  const gerar = useGerar(setBuscando, setAlerta);
  useEffect(() => {
    api.get(`/empresa/${empresa.cnpj}`, { params: { ano, mes, tipo } })
      .then((r) => setDocs(r.data))
      .catch((e) => { setAlerta({ titulo: "Não foi possível carregar os documentos", msg: erroMsg(e) }); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa.cnpj, tipo, ano, mes]);
  const atualizarDoc = (novo) => setDocs((ds) => ds.map((d) => (d.id === novo.id ? { ...d, ...novo } : d)));
  const total = (docs || []).reduce((s, d) => s + Number(d.valor_total || 0), 0);

  return (
    <Overlay onClose={onClose} z="z-[9990]">
      <div className="w-full max-w-[560px] rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-border p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary">
              <FileText className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{tipo} · {MESES[mes - 1]} {ano}</p>
              <h2 className="text-base font-semibold leading-6 text-foreground">{empresa.razao_social}</h2>
              <p className="text-xs text-muted-foreground">{cnpjFmt(empresa.cnpj)}</p>
            </div>
          </div>
          <button onClick={onClose} className={btn.icon}><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-5">
          {docs === null && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando documentos...
            </div>
          )}
          {docs?.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Nenhum documento encontrado.</p>}
          {docs?.map((d) => (
            <div key={d.id} className="rounded-xl border border-border p-4 transition hover:border-primary/30">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">Nº {d.numero_documento}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Pago em {dataBR(d.data_arrecadacao)}
                    {d.data_vencimento && <> · Venc. {dataBR(d.data_vencimento)}</>}
                    {d.periodo_apuracao && <> · PA {paBR(d.periodo_apuracao)}</>}
                  </p>
                </div>
                <p className="font-display text-base font-semibold tabular-nums text-foreground">{brl(d.valor_total)}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{d.tipo_descricao}</span>
                {d.codigo_receita && <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-medium text-muted-foreground">Cód. {d.codigo_receita}</span>}
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                <AcoesDocumento doc={d} setAlerta={setAlerta} />
                <BotaoGerar doc={d} onGerar={() => gerar(d, atualizarDoc)} />
              </div>
            </div>
          ))}
        </div>

        {docs?.length > 0 && (
          <div className="flex items-center justify-between rounded-b-2xl border-t border-border bg-muted/40 px-5 py-3 text-xs">
            <span className="text-muted-foreground">{docs.length} documento(s)</span>
            <span className="font-semibold text-foreground">Total {brl(total)}</span>
          </div>
        )}
      </div>
    </Overlay>
  );
}

/* ---------------- Calendário de intervalo (2 meses) ---------------- */
const DIAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
function gradeMes(ano, mes) {
  const primeiro = new Date(ano, mes, 1);
  const offset = (primeiro.getDay() + 6) % 7;
  const inicio = new Date(ano, mes, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i));
}
const mesmoDia = (a, b) => a && b && a.toDateString() === b.toDateString();
const fmtBR = (d) => (d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}` : "");
export { fmtBR };

export function RangeCalendar({ valor, onSelecionar, onCancelar }) {
  const hoje = new Date();
  const [base, setBase] = useState(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const [ini, setIni] = useState(valor?.[0] || null);
  const [fim, setFim] = useState(valor?.[1] || null);
  const [hover, setHover] = useState(null);
  const clicar = (d) => {
    if (!ini || (ini && fim)) { setIni(d); setFim(null); }
    else if (d < ini) { setIni(d); }
    else setFim(d);
  };
  const noRange = (d) => {
    const f = fim || hover;
    return ini && f && d > ini && d < f;
  };
  const Mes = ({ offset }) => {
    const m = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    return (
      <div className="w-[238px]">
        <div className="mb-2 flex h-8 items-center justify-between">
          {offset === 0 ? <button onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() - 1, 1))} className={btn.icon}><ChevronLeft className="h-4 w-4" /></button> : <span className="w-8" />}
          <span className="text-sm font-semibold text-foreground">{MESES[m.getMonth()]} {m.getFullYear()}</span>
          {offset === 1 ? <button onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() + 1, 1))} className={btn.icon}><ChevronRight className="h-4 w-4" /></button> : <span className="w-8" />}
        </div>
        <div className="grid grid-cols-7 text-center text-xs">
          {DIAS.map((d) => <div key={d} className="py-1 text-[10px] font-medium uppercase text-muted-foreground">{d}</div>)}
          {gradeMes(m.getFullYear(), m.getMonth()).map((d, i) => {
            const fora = d.getMonth() !== m.getMonth();
            const sel = mesmoDia(d, ini) || mesmoDia(d, fim);
            const cls = fora
              ? "text-muted-foreground/40"
              : sel
                ? "bg-primary text-primary-foreground font-semibold rounded-lg"
                : noRange(d)
                  ? "bg-primary/10 text-primary"
                  : `text-foreground hover:bg-muted rounded-lg ${mesmoDia(d, hoje) ? "ring-1 ring-primary/40" : ""}`;
            return (
              <button key={i} disabled={fora} onClick={() => clicar(d)} onMouseEnter={() => setHover(d)} className={`h-8 ${cls}`}>
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  return (
    <div className="inline-block rounded-2xl border border-border bg-card p-4 shadow-xl">
      <div className="flex flex-col gap-6 sm:flex-row"><Mes offset={0} /><Mes offset={1} /></div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
        <span className="mr-auto text-xs text-muted-foreground">{ini ? `${fmtBR(ini)} até ${fmtBR(fim || ini)}` : "Selecione o início e o fim"}</span>
        <button onClick={onCancelar} className={btn.ghost}>Cancelar</button>
        <button disabled={!ini} onClick={() => onSelecionar([ini, fim || ini])} className={btn.primary}>Aplicar</button>
      </div>
    </div>
  );
}

/* ---------------- Datepicker simples ---------------- */
export function CampoData({ valor, onChange, placeholder = "dd/mm/aaaa" }) {
  const [aberto, setAberto] = useState(false);
  const [base, setBase] = useState(() => (valor ? new Date(valor + "T00:00:00") : new Date()));
  const ref = useRef();
  useEffect(() => {
    const f = (e) => ref.current && !ref.current.contains(e.target) && setAberto(false);
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);
  const m = new Date(base.getFullYear(), base.getMonth(), 1);
  return (
    <div className="relative flex-1" ref={ref}>
      <input readOnly value={valor ? dataBR(valor) : ""} placeholder={placeholder} onFocus={() => setAberto(true)} className={`${inputCls} cursor-pointer`} />
      {valor && <button onClick={() => onChange("")} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
      {aberto && (
        <div className="absolute z-40 mt-1.5 w-[260px] rounded-xl border border-border bg-card p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <button onClick={() => setBase(new Date(m.getFullYear(), m.getMonth() - 1, 1))} className={btn.icon}><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-semibold text-foreground">{MESES[m.getMonth()]} {m.getFullYear()}</span>
            <button onClick={() => setBase(new Date(m.getFullYear(), m.getMonth() + 1, 1))} className={btn.icon}><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 text-center text-xs">
            {["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => <div key={i} className="py-1 text-[10px] font-medium text-muted-foreground">{d}</div>)}
            {Array.from({ length: 42 }, (_, i) => new Date(m.getFullYear(), m.getMonth(), 1 - m.getDay() + i)).map((d, i) => {
              const iso = isoLocal(d);
              return (
                <button key={i} onClick={() => { onChange(iso); setAberto(false); }}
                  className={`h-8 rounded-lg ${d.getMonth() !== m.getMonth() ? "text-muted-foreground/40" : "text-foreground"} ${iso === valor ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-muted"}`}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Exportação ---------------- */
function carregarScript(src, pronto) {
  return new Promise((resolve, reject) => {
    if (pronto()) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Falha ao carregar " + src));
    document.head.appendChild(s);
  });
}

export async function exportar(formato, colunas, linhas, nome) {
  const cab = colunas.map((c) => c.label);
  const corpo = linhas.map((l) => colunas.map((c) => (c.exportar ? c.exportar(l) : l[c.key] ?? "")));
  if (formato === "csv") {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "﻿" + [cab, ...corpo].map((r) => r.map(esc).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `${nome}.csv`;
    a.click();
  } else if (formato === "excel") {
    await carregarScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js", () => window.XLSX);
    const XLSX = window.XLSX;
    const ws = XLSX.utils.aoa_to_sheet([cab, ...corpo]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comprovantes");
    XLSX.writeFile(wb, `${nome}.xlsx`);
  } else {
    await carregarScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", () => window.jspdf);
    await carregarScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js",
      () => window.jspdf && window.jspdf.jsPDF.API.autoTable);
    const pdf = new window.jspdf.jsPDF({ orientation: "landscape" });
    pdf.setFontSize(12);
    pdf.text(nome.replace(/_/g, " "), 14, 12);
    pdf.autoTable({ head: [cab], body: corpo, startY: 18, styles: { fontSize: 7 }, headStyles: { fillColor: [67, 56, 202] } });
    pdf.save(`${nome}.pdf`);
  }
}

/* ---------------- Toolbar ---------------- */
export function Toolbar({ colunas, visiveis, setVisiveis, linhas, nome, busca, setBusca, mostrarColunas = true, children }) {
  const [aberto, setAberto] = useState(false);
  const [expAberto, setExpAberto] = useState(false);
  const cols = colunas.filter((c) => visiveis[c.key] !== false && !c.semExport);
  const exp = (f) => { setExpAberto(false); exportar(f, cols, linhas, nome); };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="relative w-full sm:w-72">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por razão social ou CNPJ" className={`${inputCls} pl-9`} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <div className="relative">
          <button onClick={() => setExpAberto(!expAberto)} className={btn.outline}><FileDown className="h-4 w-4" /> Exportar <ChevronDown className="h-3.5 w-3.5 opacity-60" /></button>
          {expAberto && (
            <div className="absolute right-0 top-full z-30 mt-1.5 w-44 rounded-xl border border-border bg-card p-1 shadow-xl" onMouseLeave={() => setExpAberto(false)}>
              <button onClick={() => exp("excel")} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted"><FileSpreadsheet className="h-4 w-4 text-success" /> Excel</button>
              <button onClick={() => exp("pdf")} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted"><FileText className="h-4 w-4 text-destructive" /> PDF</button>
              <button onClick={() => exp("csv")} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted"><FileDown className="h-4 w-4 text-muted-foreground" /> CSV</button>
            </div>
          )}
        </div>
        {mostrarColunas && (
          <div className="relative">
            <button onClick={() => setAberto(!aberto)} className={btn.outline}><Columns3 className="h-4 w-4" /> Colunas</button>
            {aberto && (
              <div className="absolute right-0 top-full z-30 mt-1.5 w-48 rounded-xl border border-border bg-card p-2 shadow-xl" onMouseLeave={() => setAberto(false)}>
                {colunas.filter((c) => !c.fixa).map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
                    <input type="checkbox" className="accent-[hsl(var(--primary))]" checked={visiveis[c.key] !== false} onChange={(e) => setVisiveis({ ...visiveis, [c.key]: e.target.checked })} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function Paginacao({ pagina, setPagina, total, porPagina }) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const de = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const ate = Math.min(total, pagina * porPagina);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-xs text-muted-foreground">
      <span>Mostrando <b className="text-foreground">{de}–{ate}</b> de <b className="text-foreground">{total}</b></span>
      <div className="flex items-center gap-1">
        <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} className={btn.icon}><ChevronLeft className="h-4 w-4" /></button>
        {Array.from({ length: paginas }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map((p) => (
          <button key={p} onClick={() => setPagina(p)} className={`h-8 min-w-[32px] rounded-lg px-2 text-xs font-medium ${p === pagina ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>{p}</button>
        ))}
        <button disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)} className={btn.icon}><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function useBusca(linhas, campos, busca) {
  return useMemo(() => {
    const t = busca.trim().toLowerCase().replace(/[./-]/g, "");
    if (!t) return linhas;
    return linhas.filter((l) => campos.some((c) => String(l[c] ?? "").toLowerCase().replace(/[./-]/g, "").includes(t)));
  }, [linhas, campos, busca]);
}

/* ---------------- Cabeçalho da página ---------------- */
export function Cabecalho({ titulo, subtitulo, trilha, children }) {
  return (
    <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <nav className="hidden mb-1.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {trilha.map((t, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
              {t.onClick ? <button onClick={t.onClick} className="hover:text-primary">{t.label}</button> : <span className={i === trilha.length - 1 ? "text-foreground" : ""}>{t.label}</span>}
            </span>
          ))}
        </nav>
        <h1 className="font-display text-2xl tracking-tight text-foreground">{titulo}</h1>
        {subtitulo && <p className="mt-1 text-sm text-muted-foreground">{subtitulo}</p>}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </header>
  );
}

/* janelas renderizadas no body, por cima de sidebar e topbar */
export const Overlay = (p) => createPortal(<OverlayBase {...p} />, document.body);
export const Buscando = (p) => createPortal(<BuscandoBase {...p} />, document.body);
export const Alerta = (p) => createPortal(<AlertaBase {...p} />, document.body);
